import React, { useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Camera, CheckCircle2, AlertTriangle, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { t } from "@/lib/i18n";
// Fix Q (2026-10-13): revisión con IA del Signed DO / POD (antes «Escanear papel firmado»), ahora en inglés.
// El servidor guarda la foto como archivo signed_pod de la etapa Delivery y devuelve la carga actualizada.
export function ScanPapelDialog({ scan, cargaId, onRetry, onClose, onSaved }) {
  const [forzar, setForzar] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [saving, setSaving] = useState(false);
  const r = scan.result;
  const guardar = async (aprobado_ia, motivo_forzada) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/chofer/cargas/${cargaId}/papel`, {
        file_id: r.file_id,
        original_file_id: r.original_file_id,
        legibilidad: r.legibilidad,
        revision: r.revision,
        aprobado_ia,
        motivo_subida_forzada: motivo_forzada || null,
      });
      toast.success(t("Signed DO / POD saved"));
      onSaved(data);
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-md">
        <DialogHeader><DialogTitle>{t("Signed DO / POD")}</DialogTitle></DialogHeader>
        {scan.loading ? (
          <div className="py-10 flex flex-col items-center gap-3 text-ink-2" data-testid="scan-revisando">
            <Loader2 className="h-8 w-8 animate-spin text-ink" />
            <p className="font-medium">{t("Checking the document…")}</p>
          </div>
        ) : r ? (
          <div className="space-y-4">
            <img src={r.preview} alt={t("processed document")} className="w-full rounded-lg border border-line max-h-72 object-contain bg-surface-2" data-testid="scan-preview" />
            {/* Fix S (2026-10-14): si el servidor no encontró los bordes del papel deja la foto entera; se le avisa al chofer para que la repita. */}
            {r.encontro_contorno === false && (
              <p data-testid="scan-sin-bordes" className="text-xs text-[#7A5A10] bg-[#FBF5E6] border border-[#E9D9B0] rounded-lg p-2">{t("We could not find the edges of the paper, so the whole photo was kept. For a cleaner scan, lay the paper flat on a dark surface so it fills the frame, and take it again.")}</p>
            )}
            {r.aprobado_ia === true && (
              <div data-testid="scan-aprobado" className="rounded-lg border border-line bg-white p-3 flex items-center gap-3 font-display font-semibold text-[18px] text-ink">
                <span className="h-9 w-9 rounded-full bg-ok text-white flex items-center justify-center"><CheckCircle2 className="h-5 w-5" /></span>{t(" Document looks good")}</div>
            )}
            {r.aprobado_ia === null && (
              <div data-testid="scan-sin-revisar" className="rounded-lg border border-[#E9D9B0] bg-[#FBF5E6] p-3 text-sm text-[#7A5A10]">{t("The document could not be checked (it will be saved unchecked). You can save it anyway.")}</div>
            )}
            {r.aprobado_ia === false && !forzar && (
              <div data-testid="scan-rechazado" className="rounded-lg border border-[#E9D9B0] bg-[#FBF5E6] p-3">
                <p className="flex items-center gap-2 font-display font-semibold text-[18px] text-[#7A5A10] mb-2"><AlertTriangle className="h-5 w-5" />{t(" Check the document")}</p>
                <ul className="space-y-1">
                  {r.motivos.map((m, i) => <li key={i} className="text-sm text-ink flex items-start gap-2"><X className="h-4 w-4 text-danger mt-0.5 shrink-0" />{m}</li>)}
                </ul>
              </div>
            )}
            {r.aprobado_ia === false && forzar && (
              <div className="space-y-2">
                <Label className="eyebrow">{t("Reason to upload it anyway")}</Label>
                <Textarea data-testid="scan-forzar-motivo" value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} placeholder={t("Explain why you are uploading it anyway")} />
              </div>
            )}
            <div className="space-y-2">
              {r.aprobado_ia === true && (
                <Button data-testid="scan-guardar" onClick={() => guardar(true, null)} disabled={saving} className="w-full h-12">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Save")}</Button>
              )}
              {r.aprobado_ia === null && (
                <Button data-testid="scan-guardar" onClick={() => guardar(null, null)} disabled={saving} className="w-full h-12">{saving ? <Loader2 className="h-5 w-5 animate-spin" /> : t("Save")}</Button>
              )}
              {r.aprobado_ia === false && !forzar && (
                <>
                  <Button data-testid="scan-reintentar" onClick={onRetry} className="w-full h-12"><Camera className="h-5 w-5 mr-2" />{t(" Take it again")}</Button>
                  <Button data-testid="scan-subir-igual" onClick={() => setForzar(true)} variant="ghost" className="w-full h-10 text-sm text-ink-2">{t("Upload anyway")}</Button>
                </>
              )}
              {r.aprobado_ia === false && forzar && (
                <div className="flex gap-2">
                  <Button variant="outline" className="h-12 flex-1" onClick={() => setForzar(false)}>{t("Back")}</Button>
                  <Button data-testid="scan-forzar-guardar" onClick={() => { if (!motivo.trim()) { toast.error(t("Write a reason")); return; } guardar(false, motivo.trim()); }} disabled={saving} className="h-12 flex-1 bg-danger hover:bg-[#A92825] text-white">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("Upload anyway")}</Button>
                </div>
              )}
              {!(r.aprobado_ia === false && forzar) && (
                <Button variant="outline" className="w-full h-11" onClick={onClose}>{t("Cancel")}</Button>
              )}
            </div>
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
